/**
 * The two marks that say *who* and *which brand*, drawn the same way everywhere.
 *
 * Both have a drawn fallback rather than a placeholder, because a console where
 * three of four faces are a grey silhouette is worse than one with no faces at
 * all. A person with no picture gets their initials on a colour derived from
 * their id; a brand with no mark gets its monogram on the brand's own colour.
 * Neither ever renders an empty box.
 *
 * The brand mark is inline SVG that an operator supplied, so it is set with
 * `dangerouslySetInnerHTML` and that is not a shortcut. It is sanitised
 * server-side by `sanitiseBrandMark` before it is ever stored, with an
 * allowlist that drops script, event handlers, `<foreignObject>`, `<use>`,
 * `<image>` and `<style>`. This component is the only place that markup is
 * rendered and it renders it into a `<span>` with no other children, so there
 * is one line to audit rather than a habit spread across the app.
 */
import { useState } from 'react'
import { isRasterMark } from '@gojibasel/shared/brand-mark'
import { cn } from '../lib/cn'
import { brandTone } from '../lib/brandTone'
import { initials } from '../lib/format'

type MarkSize = 'xs' | 'sm' | 'md' | 'lg'

const BOX: Record<MarkSize, string> = {
  xs: 'h-5 w-5 text-[0.5rem]',
  sm: 'h-6 w-6 text-2xs',
  md: 'h-8 w-8 text-xs',
  lg: 'h-14 w-14 text-base',
}

/**
 * A person's size. The four measured steps are 24 (table cells), 32, 36 (the
 * sidebar user row) and 40 (the contact header). The older names still work:
 * `xs` and `sm` are the 24 cell size, `md` is 32, and `lg` stays the 56 the
 * profile dialog draws, which has no Salesforge counterpart.
 */
export type AvatarSize = MarkSize | 24 | 32 | 36 | 40

const AVATAR_BOX: Record<string, string> = {
  '24': 'h-6 w-6 text-xs',
  '32': 'h-8 w-8 text-base',
  '36': 'h-9 w-9 text-base',
  '40': 'h-10 w-10 text-base',
  xs: 'h-6 w-6 text-xs',
  sm: 'h-6 w-6 text-xs',
  md: 'h-8 w-8 text-base',
  lg: 'h-14 w-14 text-title',
}

export function avatarBox(size: AvatarSize): string {
  return AVATAR_BOX[String(size)] ?? AVATAR_BOX.sm ?? ''
}

export interface AvatarProps {
  name: string
  email?: string
  src?: string | null
  size?: AvatarSize
  className?: string
  /** 'contain' draws a company logo whole on white, rather than cutting it to the circle. */
  fit?: 'cover' | 'contain'
}

/**
 * The picture an avatar draws, or null for the initials.
 *
 * A source that failed to load is not drawn again. The serve route answers 404
 * for a picture whose file is gone from disk, and for one asked under a brand
 * that cannot see the person, and an `<img>` that fails draws a broken image
 * where the initials belong. A different source is a new try, so a picture
 * that changes after a failure still shows.
 */
export function avatarSource(src: string | null | undefined, failed: string | null): string | null {
  if (!src) return null
  return src === failed ? null : src
}

/**
 * A person, as a circle. With no picture, their initials in semibold muted
 * ink on the selected grey, which is the measured look and deliberately
 * quieter than a coloured disc: a column of avatars should not outweigh the
 * names beside them. With a picture, the picture, with a hairline of black at
 * 8% inside the edge so a white background does not bleed into the page. A
 * picture that does not load falls back to the initials (`avatarSource`).
 */
export function Avatar({ name, email, src, size = 'sm', className, fit = 'cover' }: AvatarProps) {
  const label = name || email || 'Someone'
  const box = cn('relative inline-block shrink-0 overflow-hidden rounded-full', avatarBox(size), className)
  const [failed, setFailed] = useState<string | null>(null)
  const picture = avatarSource(src, failed)

  if (picture !== null) {
    return (
      <span aria-hidden="true" className={box}>
        <img
          src={picture}
          alt=""
          // Decorative: every place this is used names the person in text beside
          // it, so announcing the picture too would read the name twice.
          aria-hidden="true"
          // A logo is drawn whole on the muted grey, so a white logo on a clear background still shows.
          className={fit === 'contain' ? 'h-full w-full bg-surface-muted object-contain p-0.5' : 'h-full w-full object-cover'}
          onError={() => setFailed(picture)}
        />
        <span className="pointer-events-none absolute inset-0 rounded-full ring-1 ring-inset ring-black/[0.08]" />
      </span>
    )
  }

  return (
    <span
      aria-hidden="true"
      className={cn(box, 'grid place-items-center bg-surface-selected font-semibold text-ink-muted')}
    >
      {initials(label)}
    </span>
  )
}

export interface BrandMarkProps {
  brandId: string | undefined
  name: string
  logoSvg?: string | null
  size?: MarkSize
  className?: string
}

/**
 * A brand's mark, tinted with that brand's stable colour.
 *
 * The supplied SVG is drawn with `currentColor` by convention, so the tint is
 * applied by setting the text colour on the wrapper rather than by touching the
 * markup. A mark that hardcodes its own colours simply keeps them, which is the
 * right outcome for a real logo.
 */
export function BrandMark({ brandId, name, logoSvg, size = 'sm', className }: BrandMarkProps) {
  const tone = brandTone(brandId)
  const box = cn('grid shrink-0 place-items-center rounded-md', BOX[size], className)

  // A picture is drawn as a picture. `isRasterMark` is the same test the server
  // used when it decided to store this untouched, so the two cannot disagree
  // about which kind of mark this is and hand a data URI to `innerHTML`.
  if (logoSvg && isRasterMark(logoSvg)) {
    return (
      <img
        src={logoSvg}
        alt=""
        aria-hidden="true"
        // `contain`, not `cover`: a logo is a shape with a silhouette, and
        // cropping one to fill a square cuts the ends off a wordmark.
        className={cn(box, 'overflow-hidden bg-surface-muted object-contain p-0.5 ring-1 ring-line')}
      />
    )
  }

  if (logoSvg) {
    return (
      <span
        aria-hidden="true"
        className={cn(box, 'bg-surface-muted ring-1 ring-line', tone.text, '[&>svg]:h-[70%] [&>svg]:w-[70%]')}
        dangerouslySetInnerHTML={{ __html: logoSvg }}
      />
    )
  }

  return (
    <span aria-hidden="true" className={cn(box, tone.bar, 'font-semibold text-white')}>
      {monogram(name)}
    </span>
  )
}

/**
 * One or two letters from the brand name.
 *
 * Two words give two letters, one word gives one, because "Bl" reads as a
 * truncation and "B" reads as a mark.
 */
export function monogram(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean)
  if (words.length === 0) return '?'
  if (words.length === 1) return (words[0] ?? '?').slice(0, 1).toUpperCase()
  return `${(words[0] ?? '').slice(0, 1)}${(words[1] ?? '').slice(0, 1)}`.toUpperCase()
}
